import React, { useState, useRef, useEffect } from 'react';
import { KeyRound, ExternalLink, ArrowLeft, Eye, Monitor } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

const DESIGN_LABELS = {
  kfz1:    { name: 'Design 1', colors: ['#0f1f38', '#1a56db', '#ffffff'] },
  kfz2:    { name: 'Design 2', colors: ['#1e3a5f', '#3b82f6', '#f0f4f8'] },
  kfz3:    { name: 'Design 3', colors: ['#1e3a5f', '#1a2316', '#3b82f6'] },
  bold:    { name: 'Bold',     colors: ['#7c3aed', '#1f1f2e', '#a855f7'] },
  minimal: { name: 'Minimal',  colors: ['#b8a07e', '#fafaf8', '#1a1a1a'] },
  modern:  { name: 'Modern',   colors: ['#2563eb', '#1e40af', '#f8fafc'] },
};

function getDesignMeta(key) {
  return DESIGN_LABELS[key] ?? {
    name: key.charAt(0).toUpperCase() + key.slice(1),
    colors: ['#6366f1', '#8b5cf6', '#f8faff'],
  };
}

function DesignCard({ designKey, clientFolder, onSelect, t, cardBg, cardBorder, textPrimary, textSecondary }) {
  const meta = getDesignMeta(designKey);
  const [c0, c1, c2] = meta.colors;
  const [hov, setHov] = useState(false);

  return (
    <div
      style={{
        background: cardBg, border: `1px solid ${hov ? '#6366f1' + '40' : cardBorder}`,
        borderRadius: 16, overflow: 'hidden', transition: 'border-color 0.2s, transform 0.2s',
        transform: hov ? 'translateY(-3px)' : 'none', display: 'flex', flexDirection: 'column',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* Mockup */}
      <div style={{ height: 130, background: `linear-gradient(135deg, ${c0}, ${c1})`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 10, left: 10, right: 10, height: 18, borderRadius: 6, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: 5, padding: '0 8px' }}>
          {['#f87171','#fbbf24','#4ade80'].map(c => <div key={c} style={{ width: 7, height: 7, borderRadius: '50%', background: c + 'cc' }} />)}
        </div>
        <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ height: 7, borderRadius: 4, width: '70%', background: c2 + '40' }} />
          <div style={{ height: 7, borderRadius: 4, width: '50%', background: c2 + '28' }} />
          <div style={{ height: 7, borderRadius: 4, width: '60%', background: c2 + '18' }} />
        </div>
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', flex: 1, gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontFamily: 'monospace', color: textSecondary, marginBottom: 3 }}>{designKey}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary }}>{meta.name}</div>
        </div>
        <button
          onClick={() => onSelect(designKey, clientFolder)}
          style={{
            padding: '9px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${c0}, ${c1})`,
            color: '#fff', fontSize: 13, fontWeight: 700,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {t('preview.viewDesign')} →
        </button>
      </div>
    </div>
  );
}

export default function ClientPreview({ darkMode = false }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [step, setStep] = useState('enter-code');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientData, setClientData] = useState(null);
  const [activeDesign, setActiveDesign] = useState(null);
  const [focused, setFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 520);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 520);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(15,15,26,0.5)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.92)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const inputBg     = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  // Browser-Zurück-Button: History-State bei Schrittwechsel pushen
  const goToStep = (nextStep) => {
    window.history.pushState({ previewStep: nextStep }, '');
    setStep(nextStep);
  };

  useEffect(() => {
    const onPopState = (e) => {
      const prev = e.state?.previewStep;
      if (prev === 'select-design') { setStep('select-design'); setActiveDesign(null); }
      else { setStep('enter-code'); setCode(''); setClientData(null); setActiveDesign(null); setError(''); }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Lokale Dev-Daten – nur aktiv wenn Backend nicht erreichbar ist
  const DEV_CLIENTS = {
    'kirilenko': { clientName: 'Kirilenko', clientFolder: 'kirilenko', designs: ['kfz1', 'kfz2', 'kfz3'] },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/preview/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });

      // Gültige Backend-Antwort oder Dev-Fallback
      let data = null;
      if (res.ok) {
        try { data = await res.json(); } catch {}
      }
      if (!data || !data.clientName) {
        const dev = DEV_CLIENTS[code.trim().toLowerCase()];
        if (dev) { data = dev; }
        else { setError(res.status === 404 ? t('preview.errorInvalid') : t('preview.errorServer')); return; }
      }

      const designs = Array.isArray(data.designs) ? data.designs : [];
      setClientData({ clientName: data.clientName, clientFolder: data.clientFolder, designs });
      if (designs.length === 0 || !data.clientFolder) {
        setActiveDesign({ key: null, url: `/previews/${code.trim().toUpperCase()}/index.html` });
        goToStep('viewing');
      } else if (designs.length === 1) {
        setActiveDesign({ key: designs[0], url: `/previews/${data.clientFolder}/${designs[0]}/` });
        goToStep('viewing');
      } else {
        goToStep('select-design');
      }
    } catch {
      // Fetch komplett fehlgeschlagen (Backend offline) → Dev-Fallback
      const dev = DEV_CLIENTS[code.trim().toLowerCase()];
      if (dev) {
        setClientData(dev);
        if (dev.designs.length === 1) {
          setActiveDesign({ key: dev.designs[0], url: `/previews/${dev.clientFolder}/${dev.designs[0]}/` });
          goToStep('viewing');
        } else {
          goToStep('select-design');
        }
      } else {
        setError(t('preview.errorInvalid'));
      }
    }
    finally  { setLoading(false); }
  };

  const handleSelectDesign = (designKey, clientFolder) => {
    setActiveDesign({ key: designKey, url: `/previews/${clientFolder}/${designKey}/` });
    goToStep('viewing');
  };

  const handleReset = () => {
    setStep('enter-code'); setCode(''); setError(''); setClientData(null); setActiveDesign(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Viewer ──────────────────────────────────────────────────────────────────
  if (step === 'viewing' && activeDesign) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: bg }}>
        <div style={{
          padding: '0 10px', minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 6,
          background: darkMode ? 'rgba(10,10,15,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)', borderBottom: `1px solid ${cardBorder}`, flexShrink: 0,
        }}>
          {/* Left: back within preview flow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexShrink: 0 }}>
            <button
              onClick={() => clientData?.designs?.length > 1 ? setStep('select-design') : handleReset()}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: textSecondary, whiteSpace: 'nowrap',
                padding: '8px 10px', borderRadius: 8, transition: 'color 0.2s, background 0.2s',
                background: 'none', border: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.background = accent + '10'; }}
              onMouseLeave={e => { e.currentTarget.style.color = textSecondary; e.currentTarget.style.background = 'none'; }}
            >
              <ArrowLeft size={14} />
              {!isMobile && (clientData?.designs?.length > 1 ? t('preview.backToSelection') : t('preview.backBtn'))}
            </button>
            {!isMobile && activeDesign.key && (
              <span style={{ fontSize: 13, fontWeight: 600, color: accent, whiteSpace: 'nowrap' }}>
                {getDesignMeta(activeDesign.key).name}
              </span>
            )}
          </div>

          {/* Right: back to website + open fullscreen */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <a href="/" style={{
              display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 700,
              color: accent, textDecoration: 'none', padding: '8px 12px', borderRadius: 8,
              background: accent + '12', border: `1px solid ${accent}30`, transition: 'background 0.2s',
              whiteSpace: 'nowrap',
            }}
              onMouseEnter={e => e.currentTarget.style.background = accent + '22'}
              onMouseLeave={e => e.currentTarget.style.background = accent + '12'}
            >
              <ArrowLeft size={13} />
              {isMobile ? 'Website' : '← derrdigital.de'}
            </a>
            <a href={activeDesign.url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: textSecondary, textDecoration: 'none', padding: '8px 10px', borderRadius: 8, background: 'transparent', border: `1px solid ${cardBorder}`, transition: 'background 0.2s, color 0.2s', whiteSpace: 'nowrap', flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = accent + '10'; e.currentTarget.style.color = accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = textSecondary; }}
            >
              <ExternalLink size={13} />
              {!isMobile && t('preview.openFullscreen')}
            </a>
          </div>
        </div>
        <iframe src={activeDesign.url} title={activeDesign.key ?? 'Preview'} style={{ flex: 1, width: '100%', border: 'none' }} />
      </div>
    );
  }

  // ── Design-Auswahl ──────────────────────────────────────────────────────────
  if (step === 'select-design' && clientData) {
    return (
      <div style={{ background: bg, color: textPrimary, minHeight: '100vh', padding: 'clamp(80px,12vh,120px) 24px 80px', position: 'relative' }}>
        <a href="/" style={{
          position: 'absolute', top: 16, left: 16,
          display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600,
          color: textSecondary, textDecoration: 'none', padding: '7px 12px', borderRadius: 8,
          background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          border: `1px solid ${cardBorder}`, transition: 'color 0.2s, background 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.background = accent + '12'; }}
          onMouseLeave={e => { e.currentTarget.style.color = textSecondary; e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; }}
        >
          <ArrowLeft size={13} /> derrdigital.de
        </a>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, ${accent}, ${accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: `0 6px 20px ${accent}35` }}>
              <Eye size={22} color="#fff" />
            </div>
            <h1 style={{ fontSize: 'clamp(20px, 3vw, 32px)', fontWeight: 900, marginBottom: 6 }}>{clientData.clientName}</h1>
            <p style={{ fontSize: 14, color: textSecondary }}>{t('preview.selectSubtitle')}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 18 }}>
            {clientData.designs.map(key => (
              <DesignCard key={key} designKey={key} clientFolder={clientData.clientFolder}
                onSelect={handleSelectDesign} t={t}
                cardBg={cardBg} cardBorder={cardBorder} textPrimary={textPrimary} textSecondary={textSecondary} />
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 28 }}>
            <button onClick={handleReset} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: textSecondary, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = accent}
              onMouseLeave={e => e.currentTarget.style.color = textSecondary}
            >{t('preview.backBtn')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Code-Eingabe ────────────────────────────────────────────────────────────
  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative' }}>
      <a href="/" style={{
        position: 'absolute', top: 16, left: 16,
        display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600,
        color: textSecondary, textDecoration: 'none', padding: '7px 12px', borderRadius: 8,
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        border: `1px solid ${cardBorder}`, transition: 'color 0.2s, background 0.2s',
      }}
        onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.background = accent + '12'; }}
        onMouseLeave={e => { e.currentTarget.style.color = textSecondary; e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; }}
      >
        <ArrowLeft size={13} /> derrdigital.de
      </a>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo + title */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${accent}, ${accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: `0 8px 24px ${accent}35` }}>
            <Monitor size={24} color="#fff" />
          </div>
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 8 }}>{t('preview.pageTitle')}</h1>
          <p style={{ fontSize: 14, color: textSecondary, lineHeight: 1.6 }}>{t('preview.pageSubtitle')}</p>
        </div>

        {/* Card */}
        <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, padding: '24px 22px', backdropFilter: 'blur(16px)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accent}, ${accent2}, transparent)` }} />

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                <KeyRound size={11} /> Code
              </label>
              <input
                ref={inputRef}
                type="text"
                value={code}
                onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
                placeholder={t('preview.codePlaceholder')}
                autoFocus
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                  width: '100%', padding: '13px 16px', borderRadius: 11, fontSize: 18,
                  fontFamily: 'monospace', letterSpacing: '0.12em', fontWeight: 700,
                  background: inputBg, color: textPrimary, outline: 'none',
                  border: `1px solid ${focused ? accent : cardBorder}`,
                  boxShadow: focused ? `0 0 0 3px ${accent}20` : 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 12px', borderRadius: 9, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', fontSize: 13, color: '#ef4444', fontWeight: 600 }}>
                <span>!</span>{error}
              </div>
            )}

            <button
              type="submit"
              disabled={!code.trim() || loading}
              style={{
                padding: '13px', borderRadius: 11, border: 'none', cursor: code.trim() && !loading ? 'pointer' : 'not-allowed',
                background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                color: '#fff', fontWeight: 700, fontSize: 15,
                opacity: !code.trim() || loading ? 0.55 : 1,
                boxShadow: code.trim() && !loading ? `0 6px 20px ${accent}35` : 'none',
                transition: 'opacity 0.2s, transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={e => { if (code.trim() && !loading) e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {loading ? t('preview.loading') : t('preview.submitBtn')}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: textSecondary, marginTop: 20 }}>
          © {new Date().getFullYear()} DerrDigital
        </p>
      </div>
    </div>
  );
}
